import { useState, useEffect, type RefObject } from 'react'

interface UseBackgroundContrastOptions {
  containerRef?: RefObject<HTMLElement | null>
}

/**
 * Parses a color string and returns RGB values
 */
function parseColor(color: string): { r: number; g: number; b: number; a: number } | null {
  // Handle rgb/rgba
  const rgbMatch = color.match(/rgba?\s*\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/)
  if (rgbMatch) {
    return {
      r: parseFloat(rgbMatch[1]),
      g: parseFloat(rgbMatch[2]),
      b: parseFloat(rgbMatch[3]),
      a: rgbMatch[4] !== undefined ? parseFloat(rgbMatch[4]) : 1,
    }
  }

  // Handle hex colors
  const hexMatch = color.match(/#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})/)
  if (hexMatch) {
    return {
      r: parseInt(hexMatch[1], 16),
      g: parseInt(hexMatch[2], 16),
      b: parseInt(hexMatch[3], 16),
      a: 1,
    }
  }

  // Handle short hex
  const shortHexMatch = color.match(/#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])/)
  if (shortHexMatch) {
    return {
      r: parseInt(shortHexMatch[1] + shortHexMatch[1], 16),
      g: parseInt(shortHexMatch[2] + shortHexMatch[2], 16),
      b: parseInt(shortHexMatch[3] + shortHexMatch[3], 16),
      a: 1,
    }
  }

  return null
}

/**
 * Extracts the first non-transparent color from a gradient string
 */
function extractGradientColor(gradient: string): { r: number; g: number; b: number; a: number } | null {
  // Match all rgb/rgba colors in the string
  const rgbMatches = gradient.matchAll(/rgba?\s*\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+(?:\s*,\s*[\d.]+)?\s*\)/g)
  for (const match of rgbMatches) {
    const color = parseColor(match[0])
    if (color && color.a >= 0.1) {
      return color
    }
  }

  // Match hex in gradient
  const hexMatch = gradient.match(/#[0-9a-fA-F]{3,6}/)
  if (hexMatch) {
    return parseColor(hexMatch[0])
  }

  return null
}

/**
 * Detects whether the background behind a given element is dark or light.
 * Samples the pixel color from elements behind the target element.
 */
export function useBackgroundContrast(
  elementRef: RefObject<HTMLElement | null>,
  options: UseBackgroundContrastOptions = {}
): 'dark' | 'light' {
  const [background, setBackground] = useState<'dark' | 'light'>('light')

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const check = () => {
      const rect = element.getBoundingClientRect()
      const x = rect.left + rect.width / 2
      const y = rect.top + rect.height / 2

      // Hide the chatbot container (the fixed positioned parent)
      const container = element.closest('.fixed') as HTMLElement
      const originalDisplay = container?.style.display
      if (container) container.style.display = 'none'

      // Get all elements at this point
      const elements = document.elementsFromPoint(x, y)

      // Restore container
      if (container) container.style.display = originalDisplay || ''

      // Find first element with non-transparent background
      for (const el of elements) {
        const style = getComputedStyle(el)

        // Check backgroundColor first
        let color = parseColor(style.backgroundColor)

        // If backgroundColor is transparent, check for gradients
        if (!color || color.a < 0.1) {
          const bg = style.background || style.backgroundImage
          if (bg && bg.includes('gradient')) {
            color = extractGradientColor(bg)
          }
        }

        // Skip if no valid color found or too transparent
        if (!color || color.a < 0.1) continue

        // Calculate luminance
        const luminance = (0.299 * color.r + 0.587 * color.g + 0.114 * color.b) / 255

        // Use 0.45 threshold to catch more dark backgrounds
        setBackground(luminance < 0.45 ? 'dark' : 'light')
        return
      }
    }

    check()

    const observer = new IntersectionObserver(check, {
      root: options.containerRef?.current,
      threshold: [0, 0.5, 1],
    })
    observer.observe(element)

    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check, { passive: true })

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
    }
  }, [elementRef, options.containerRef])

  return background
}
